import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  Package,
  Plus,
  Edit,
  Trash2,
  LogOut,
  ArrowLeft,
  DollarSign,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import './Products.css';

const Products = () => {
  const { user, logout } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    available_for_pos: true,
    available_for_ecommerce: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'owner') {
      toast.error('Only business owners can manage products');
      navigate('/dashboard');
      return;
    }
    fetchProducts();
    // fetchProducts is stable via useCallback below
    // eslint-disable-next-line
  }, [user, navigate]);

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/products`, { headers });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        category: product.category || '',
        image_url: product.image_url || '',
        available_for_pos: product.available_for_pos,
        available_for_ecommerce: product.available_for_ecommerce
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        image_url: '',
        available_for_pos: true,
        available_for_ecommerce: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, payload, { headers });
        toast.success('Product created');
      }

      handleCloseModal();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/products/${productId}`, { headers });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          <div className="page-header">
            <Package size={32} />
            <h1>Product Catalog</h1>
            <p>Manage your products for POS and online checkout</p>
          </div>

          <div className="products-actions">
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={18} /> Add Product
            </button>
          </div>

          <div className="products-grid">
            {products.length === 0 ? (
              <div className="empty-state">
                <Package size={48} color="rgba(228, 230, 235, 0.3)" />
                <p>No products yet</p>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                  <Plus size={18} /> Create First Product
                </button>
              </div>
            ) : (
              products.map((product) => (
                <div key={product.id} className="product-card">
                  {product.image_url && (
                    <div className="product-image" style={{backgroundImage: `url(${product.image_url})`}} />
                  )}
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    {product.description && (
                      <p className="product-description">{product.description}</p>
                    )}
                    <div className="product-price">
                      <DollarSign size={18} />
                      {product.price.toFixed(2)}
                    </div>
                    {product.category && (
                      <div className="product-category">
                        <Tag size={14} />
                        {product.category}
                      </div>
                    )}
                    <div className="product-availability">
                      {product.available_for_pos && <span className="avail-badge pos">POS</span>}
                      {product.available_for_ecommerce && <span className="avail-badge ecom">E-Commerce</span>}
                    </div>
                  </div>
                  <div className="product-actions">
                    <button className="btn-icon" onClick={() => handleOpenModal(product)}>
                      <Edit size={18} />
                    </button>
                    <button className="btn-icon danger" onClick={() => handleDelete(product.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editingProduct ? 'Edit Product' : 'Create Product'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g. Food, Drinks"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.available_for_pos}
                    onChange={(e) => setFormData({...formData, available_for_pos: e.target.checked})}
                  />
                  <span>Available for POS</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.available_for_ecommerce}
                    onChange={(e) => setFormData({...formData, available_for_ecommerce: e.target.checked})}
                  />
                  <span>Available for E-Commerce</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
